import { Recycle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PackagingRecyclabilityAssessmentStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { PackagingRecyclabilityAssessmentHistoryTable } from './PackagingRecyclabilityAssessmentHistoryTable'

type PackagingItemRecyclabilityTabProps = {
  history: PackagingRecyclabilityAssessmentStoredEntry[]
  locale: string
  showRunAssessment?: boolean
  onRunAssessmentClick?: () => void
  showPreviewDocument?: boolean
  onPreviewDocumentClick?: () => void
}

function RecyclabilityTabActions({
  showRunAssessment,
  onRunAssessmentClick,
  showPreviewDocument,
  onPreviewDocumentClick,
  previewDocumentDisabled,
}: {
  showRunAssessment: boolean
  onRunAssessmentClick?: () => void
  showPreviewDocument: boolean
  onPreviewDocumentClick?: () => void
  previewDocumentDisabled: boolean
}) {
  const { t } = useTranslation('packagingSuppliers')
  const showRun = showRunAssessment && onRunAssessmentClick
  const showPreview = showPreviewDocument && onPreviewDocumentClick

  if (!showRun && !showPreview) {
    return null
  }

  return (
    <div className='flex flex-wrap items-center justify-end gap-3'>
      {showPreview && (
        <Button
          type='button'
          variant='outline'
          disabled={previewDocumentDisabled}
          onClick={onPreviewDocumentClick}
        >
          {t('itemDetail.recyclability.document.previewButton')}
        </Button>
      )}
      {showRun && (
        <Button type='button' variant='outline' onClick={onRunAssessmentClick}>
          {t('itemDetail.recyclability.runAssessment')}
        </Button>
      )}
    </div>
  )
}

export function PackagingItemRecyclabilityTab({
  history,
  locale,
  showRunAssessment = false,
  onRunAssessmentClick,
  showPreviewDocument = false,
  onPreviewDocumentClick,
}: PackagingItemRecyclabilityTabProps) {
  const { t } = useTranslation('packagingSuppliers')
  const previewDocumentDisabled = history.length === 0
  const showHint = showRunAssessment && onRunAssessmentClick
  const showRun = showRunAssessment && onRunAssessmentClick

  if (history.length === 0) {
    return (
      <div className='space-y-4'>
        {showHint ? (
          <Alert variant='info'>
            <AlertDescription>
              {t('itemDetail.recyclability.runAssessmentHint')}
            </AlertDescription>
          </Alert>
        ) : null}
        <Empty className='rounded-lg border border-dashed py-12'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Recycle className='size-10' />
            </EmptyMedia>
            <EmptyTitle>{t('itemDetail.recyclability.empty')}</EmptyTitle>
          </EmptyHeader>
          {showRun ? (
            <EmptyContent>
              <Button type='button' onClick={onRunAssessmentClick}>
                {t('itemDetail.recyclability.runAssessment')}
              </Button>
            </EmptyContent>
          ) : null}
        </Empty>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {showHint ? (
        <Alert variant='info'>
          <AlertDescription>
            {t('itemDetail.recyclability.runAssessmentHint')}
          </AlertDescription>
        </Alert>
      ) : null}
      <RecyclabilityTabActions
        showRunAssessment={showRunAssessment}
        onRunAssessmentClick={onRunAssessmentClick}
        showPreviewDocument={showPreviewDocument}
        onPreviewDocumentClick={onPreviewDocumentClick}
        previewDocumentDisabled={previewDocumentDisabled}
      />
      <PackagingRecyclabilityAssessmentHistoryTable
        history={history}
        locale={locale}
      />
    </div>
  )
}
